import React, { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { TodoListItemAssignedUsers } from "./TodoListItemAssignedUsers";
import { TodoListItemNewFooterContainer } from "./TodoListItemFooterContainer";
import { TodoListItemDeadline } from "./TodoListItemDeadline";
import { TodoListItemTags } from "./TodoListItemTags";

import { Box, useLayoutContext, useTime } from "arkh";
import { Duration, DateTime } from "luxon";

import { TodoListItemProducts } from "./TodoListItemProducts";
import { TodoListItemFooterItem } from "./TodoListItemFooterItem";

import { TodoListItemComments } from "./TodoListItemComments";
import { TodoListItemFooterSubTodos } from "./TodoListItemFooterSubTodos";
import { TodoListItemContact } from "./TodoListItemContact";

import { TodoListItemFooterPending_todo$key } from "./__generated__/TodoListItemFooterPending_todo.graphql";

export function TodoListItemFooterPending(props: {
  todo: TodoListItemFooterPending_todo$key;
  sizeClass: "compact" | "wide" | "regular";
  currentEmployeeId: string | undefined;
}) {
  const todo = useFragment(
    graphql`
      fragment TodoListItemFooterPending_todo on Todo {
        ...TodoListItemAssignedUsers_todo
        ...TodoListItemContact_todo
        feedbackStatus
        contact {
          __typename
        }
        expireAtDate {
          ...StoreFormattedDate
          isoString
        }
        tags {
          ...TodoListItemTags_tag
        }

        assigned {
          __typename
        }
        assignedRoles {
          __typename
        }
        linkedProducts {
          ...TodoListItemProducts_StoreProduct
        }
        ...TodoListItemFooterSubTodos_todo
        subTodos {
          __typename
        }
        commentCount
      }
    `,
    props.todo
  );
  const hasSubTodos = (todo.subTodos?.length ?? 0) > 0;
  const hasDeadline = todo.expireAtDate !== null;
  const hasProducts = (todo.linkedProducts?.length ?? 0) > 0;
  const hasAssignedUsers =
    todo.assigned.length > 0 || todo.assignedRoles.length > 0;
  const hasTags = todo.tags.length > 0;
  const hasContact = todo.contact !== null && todo.feedbackStatus;
  const itemsCount = useMemo(() => {
    return (
      (hasSubTodos ? 1 : 0) +
      (hasDeadline ? 1 : 0) +
      (hasProducts ? 1 : 0) +
      (hasAssignedUsers ? 1 : 0) +
      (hasTags ? 1 : 0) +
      (hasContact ? 1 : 0)
    );
  }, [
    hasSubTodos,
    hasDeadline,
    hasProducts,
    hasAssignedUsers,
    hasTags,
    hasContact,
  ]);

  const width = useMemo(() => {
    switch (props.sizeClass) {
      case "compact":
        return 400;
      case "wide":
        return 800;
      case "regular":
      default:
        return 600;
    }
  }, [props.sizeClass]);
  const maxWidth = useMemo(() => {
    return Math.max(120, width / itemsCount);
  }, [itemsCount, width]);

  const now = useTime(
    Duration.fromObject({ seconds: 30 }).as("milliseconds"),
    !todo.expireAtDate
  );
  const isExpired = useMemo(
    () =>
      !!(
        todo.expireAtDate && DateTime.fromISO(todo.expireAtDate.isoString) < now
      ),
    [todo.expireAtDate, now]
  );

  const layout = useLayoutContext();

  const items: React.ReactNode[] = [];
  if (hasSubTodos) {
    items.push(
      <TodoListItemFooterItem key="subtodos">
        <TodoListItemFooterSubTodos todo={todo} />
      </TodoListItemFooterItem>
    );
  }

  if (hasDeadline) {
    items.push(
      <TodoListItemFooterItem key="deadline">
        <TodoListItemDeadline
          expireAtFormatted={todo.expireAtDate}
          isExpired={isExpired}
        />
      </TodoListItemFooterItem>
    );
  }
  if (hasProducts) {
    items.push(
      <TodoListItemFooterItem key="products">
        <TodoListItemProducts
          products={todo.linkedProducts}
          maxWidth={maxWidth}
        />
      </TodoListItemFooterItem>
    );
  }

  if (hasAssignedUsers) {
    items.push(
      <TodoListItemAssignedUsers
        key="assignees"
        todo={todo}
        maxWidth={maxWidth}
        currentEmployeeId={props.currentEmployeeId}
      />
    );
  }
  if (hasContact) {
    items.push(<TodoListItemContact todo={todo} />);
  }

  if (hasTags) {
    items.push(
      <TodoListItemFooterItem key="tags">
        <TodoListItemTags tags={todo.tags} maxWidth={maxWidth} />
      </TodoListItemFooterItem>
    );
  }
  if (todo.commentCount > 0) {
    items.push(
      <TodoListItemFooterItem key="comments">
        <TodoListItemComments commentCount={todo.commentCount} />
      </TodoListItemFooterItem>
    );
  }

  if (items.length === 0) {
    return <Box spaceBottom="medium" />;
  }

  return (
    <Box
      style={{
        marginRight: -layout.space.medium,
        marginBottom: -layout.space.xsmall,
      }}
    >
      <TodoListItemNewFooterContainer>{items}</TodoListItemNewFooterContainer>
    </Box>
  );
}
